import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    api
      .get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-4 px-6" data-testid="product-not-found">
        <h1 className="font-serif text-4xl">Product not found</h1>
        <Link to="/shop" className="font-mono text-xs uppercase tracking-widest text-pharma">← Back to shop</Link>
      </main>
    );
  }

  if (!product) {
    return <main className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 rounded-full border-2 border-ink/20 border-t-pharma animate-spin" /></main>;
  }

  const isSkin = product.brand === "oneskin";
  const accent = isSkin ? "text-derma" : "text-pharma";

  return (
    <main data-testid="product-detail-page" className="pt-28 md:pt-32 pb-24 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate(-1)} data-testid="product-back" className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-ink/60 hover:text-ink transition-colors mb-10">
          <ArrowLeft size={14} /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)", scale: 1.1 }}
            animate={{ clipPath: "inset(0% 0 0 0)", scale: 1 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative aspect-[4/5] rounded-[2rem] overflow-hidden bg-surfalt lg:sticky lg:top-28"
          >
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          </motion.div>

          <div className="flex flex-col gap-8 lg:py-6">
            <div className="flex items-center gap-3">
              <span className={`w-2 h-2 rounded-full ${isSkin ? "bg-derma" : "bg-pharma"}`} />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50">
                {isSkin ? "One Skin" : "OneKind"} · {product.category}
              </span>
            </div>

            <h1 className="font-serif text-5xl md:text-7xl leading-[0.9] tracking-tighter">
              <MaskedLines animate lines={[product.name]} />
            </h1>

            <Reveal delay={0.1}>
              <p className={`font-serif italic text-2xl ${accent}`}>{product.tagline}</p>
            </Reveal>

            <Reveal delay={0.15}>
              <p className="font-sans text-base md:text-lg text-ink/70 leading-relaxed">{product.description}</p>
            </Reveal>

            <div className="flex flex-col gap-3 border-y border-ink/10 py-6">
              {product.benefits?.map((b, i) => (
                <div key={i} className="flex items-center gap-3 font-sans text-sm">
                  <Check size={16} className={accent} /> {b}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <span className="font-serif text-4xl">${product.price.toFixed(2)}</span>
              <span className="font-mono text-xs text-ink/40 uppercase tracking-widest">{product.currency}</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => { add(product); toast.success(`${product.name} added to your selection.`); }}
                data-testid="detail-add-to-cart"
                className="flex-1 rounded-full py-4 bg-ink text-stone text-xs uppercase tracking-widest font-sans hover:bg-pharma transition-colors"
              >
                Add to Selection
              </button>
              {product.external_url && (
                <a
                  href={product.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="detail-external-link"
                  className="flex-1 flex items-center justify-center gap-2 rounded-full py-4 border border-derma text-derma text-xs uppercase tracking-widest font-sans hover:bg-derma hover:text-stone transition-colors"
                >
                  Buy on inoneskin.com <ArrowUpRight size={14} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
